import React from "react";
import { Cpu, GraduationCap, Lamp, PencilRuler, ArrowUpRight } from "lucide-react";
import { WHATSAPP, openExternal } from "@/lib/contact";
import Reveal from "@/components/Reveal";

const SERVICES = [
  {
    id: "engineering",
    icon: Cpu,
    title: "Precision Functional Parts",
    desc: "Functional casings, robotic linkages, assemblies and custom mechatronics parts. Accuracy depends on geometry, material, printer and orientation.",
    tags: ["Precise fits", "PETG · Nylon-CF", "Press / snap fits"],
  },
  {
    id: "college",
    icon: GraduationCap,
    title: "College & STEM Projects",
    desc: "Budget-friendly prints for drone arms, IoT enclosures, custom mounts and final-year submissions. Student bundles available.",
    tags: ["Drone arms", "IoT cases", "Lab kits"],
  },
  {
    id: "decor",
    icon: Lamp,
    title: "Home Decor & Lighting",
    desc: "Low-poly vases, pendant lights, sculptural wall plates, organizers and artistic prints in Silk-PLA and translucent finishes.",
    tags: ["Vase mode", "Translucent", "Custom scale"],
  },
  {
    id: "cad",
    icon: PencilRuler,
    title: "CAD & Product Design",
    desc: "Reverse engineering, STL mesh repairs, DFM optimization and raw concept drafting in Fusion 360 / SolidWorks / Onshape.",
    tags: ["STL repair", "DFM review", "Reverse eng."],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <Reveal className="lg:col-span-4">
            <span className="label-overline">/ 01 — Capabilities</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] font-semibold tracking-tight">
              Built for makers,<br />engineered for precision.
            </h2>
          </Reveal>
          <Reveal delay={140} className="lg:col-span-7 lg:col-start-6 self-end">
            <p className="text-zinc-400 text-lg leading-relaxed">
              Four core service lines, every brief handled in-house — from
              concept sketch to a courier dispatched the same week. We optimise
              the print for cost and function before we ever heat the nozzle.
            </p>
          </Reveal>
        </div>

        {/* Bento grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 border border-white/10">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.id}
                delay={idx * 90}
                className={`bg-[#0F0F11] transition-colors duration-300 hover:bg-[#121214]
                  border-white/10
                  ${idx < 3 ? "lg:border-r" : ""}
                  ${idx < 2 ? "sm:border-r lg:border-r" : "sm:border-r-0 lg:border-r"}
                  ${idx === SERVICES.length - 1 ? "lg:border-r-0" : ""}
                  ${idx < 2 ? "border-b sm:border-b lg:border-b-0" : ""}
                `}
              >
                <div data-testid={`service-card-${s.id}`} className="group relative p-8 sm:p-9 h-full">
                  <div className="w-12 h-12 grid place-items-center border border-white/15 group-hover:border-[#FF5722] group-hover:text-[#FF5722] group-hover:rotate-6 transition-all duration-500 mb-8">
                    <Icon size={20} />
                  </div>
                  <div className="label-overline mb-3">0{idx + 1}</div>
                  <h3 className="font-display text-2xl font-medium mb-3 group-hover:text-white">
                    {s.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                    {s.desc}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className="font-mono text-[10px] px-2 py-1 border border-white/10 text-zinc-400"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <a
                    data-testid={`service-cta-${s.id}`}
                    href={WHATSAPP}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => openExternal(e, WHATSAPP)}
                    className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 group-hover:text-[#FF5722] transition-colors"
                  >
                    Get quote
                    <ArrowUpRight size={14} />
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
